import {
  ITERABLE_SMS,
  MESSAGING_METRICS,
  SMS_METRICS,
  TELNYX_DIRECT,
  TWILIO_DIRECT,
} from 'modules/billing/constants';
import { getMetricValue } from 'modules/billing/utils/metrics';
import { BillingParty, OrgMetric, OrgProviderBillingPartyMetric } from 'modules/domains/billing';
import { SERVICE_PROVIDERS } from 'modules/graphql';
import { sumObjectListByKey } from 'utils/arrayHelpers';
import { TableColumn, TableMetric } from '../AnalyticTable';

const orderedEmailMetrics = [
  { metricName: MESSAGING_METRICS.emailsSent, label: 'To sends' },
  { metricName: MESSAGING_METRICS.ccEmailsSent, label: 'CC sends' },
  { metricName: MESSAGING_METRICS.bccEmailsSent, label: 'BCC sends' },
];

export const createEmailTableRows = (orgMetricsByName: Record<string, OrgMetric>) => {
  return orderedEmailMetrics.map(({ label, metricName }) => ({
    metricName: { label },
    values: [{ value: getMetricValue(orgMetricsByName, metricName) }],
  }));
};

/** List of SMS providers with formatted name for display and any description text necessary */
const smsProviders = [
  { description: '', id: SERVICE_PROVIDERS.ITERABLE, providerName: ITERABLE_SMS },
  { description: '', id: SERVICE_PROVIDERS.TELNYX, providerName: TELNYX_DIRECT },
  { description: '', id: SERVICE_PROVIDERS.TWILIO, providerName: TWILIO_DIRECT },
];

/** SMS metrics in display order */
const orderedSmsMetrics = [
  { metricName: SMS_METRICS.smsSegmentsSent, label: 'SMS segments sent' },
  { metricName: SMS_METRICS.smsSegmentsReceived, label: 'SMS segments received' },
  { metricName: SMS_METRICS.smsSegmentsTotal, label: 'SMS segments total' },
  { metricName: SMS_METRICS.mmsAttachmentsSent, label: 'MMS sent' },
  { metricName: SMS_METRICS.mmsAttachmentsReceived, label: 'MMS received' },
  { metricName: SMS_METRICS.mmsAttachmentsTotal, label: 'MMS total' },
  { metricName: SMS_METRICS.successfulSmsVerifications, label: 'SMS verifications' },
];

export const getProviderName = (metric: OrgProviderBillingPartyMetric) =>
  metric.billingParty === BillingParty.ThirdParty
    ? SERVICE_PROVIDERS.ITERABLE
    : metric.providerName;

export const createSmsProviders = (smsMetrics: OrgProviderBillingPartyMetric[]) => {
  /** Unique list of sms providers */
  const uniqueProviders = Array.from(
    new Set(
      smsMetrics.map((provider) =>
        provider.billingParty === BillingParty.ThirdParty
          ? SERVICE_PROVIDERS.ITERABLE
          : provider.providerName,
      ),
    ),
  );

  /**
   * List of SMS providers for this organization
   * `smsProviders` is source of truth to provide display order and hold additional
   * context such as `description` if necessary
   *
   * Filter list of defined SMS providers based on if they do/don't appear in the response
   */
  return smsProviders.filter((provider) => uniqueProviders.includes(provider.id));
};

export const createSmsTableRows = (smsMetrics: OrgProviderBillingPartyMetric[]): TableMetric[] => {
  const result = smsMetrics.reduce(
    (acc, curr) => {
      const { metricName, value } = curr;
      // distinguish Iterable SMS from other SMS Providers
      const providerName = getProviderName(curr);

      // current or default values
      const currentMetric = acc[metricName] || {};
      const currentValues = acc?.[metricName]?.values || {};
      const currentValue = currentValues?.[providerName] || 0;

      return {
        ...acc,
        [metricName]: {
          ...currentMetric,
          metricName,
          values: {
            ...currentValues,
            [providerName]: currentValue + value,
          },
        },
      };
    },
    {} as Record<string, { metricName: string; values: Record<string, number> }>,
  );

  const columnProviders = createSmsProviders(smsMetrics);
  const tableRows = orderedSmsMetrics.map(({ label, metricName }) => {
    const providerValues = columnProviders.map((provider) => ({
      isTotal:
        metricName === SMS_METRICS.smsSegmentsTotal ||
        metricName === SMS_METRICS.mmsAttachmentsTotal,
      value: result[metricName].values[provider.id],
    }));

    const sumValue = sumObjectListByKey(providerValues, 'value');

    return {
      metricName: { label },
      values: [...providerValues, { isTotal: true, value: sumValue }],
    };
  });

  return tableRows;
};

export const createSmsTableColumns = (
  smsMetrics: OrgProviderBillingPartyMetric[],
): TableColumn[] => {
  const columnProviders = createSmsProviders(smsMetrics);

  /** converts list of providers from one format to another suitable for the analytic table */
  const columns: TableColumn[] = [
    // always show a metric name column
    { label: 'SMS breakdown' },
    // show a column for each provider if present
    ...columnProviders.map((provider) => ({
      description: provider.description,
      label: provider.providerName,
      isNumeric: true,
    })),
    // always show a total column
    { label: 'Total', isNumeric: true },
  ];

  return columns;
};
